import React, { Component } from "react";

/**
 * Cloudflare Turnstile Component for Kiosk
 * 
 * This component integrates Cloudflare Turnstile challenge widget for bot protection.
 * It supports both visible and invisible modes.
 * 
 * Props:
 * - onVerify: Callback function called when verification is successful with the token
 * - onError: Callback function called when an error occurs
 * - onExpire: Callback function called when the token expires
 * - theme: "light" | "dark" | "auto" (default: "auto")
 * - size: "normal" | "compact" (default: "normal")
 * - action: Custom action identifier (optional)
 * - cData: Custom data to include in the token (optional)
 */
class CloudflareTurnstile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isVerifying: false,
      isVerified: false,
      error: null,
      widgetId: null,
    };
    this.containerRef = React.createRef();
    this.turnstileLoaded = false;
  }

  componentDidMount() {
    this.loadTurnstileScript();
  }

  componentWillUnmount() {
    // Clean up the widget when component unmounts
    if (this.state.widgetId !== null && window.turnstile) {
      try {
        window.turnstile.remove(this.state.widgetId);
      } catch (error) {
        console.error("Error removing Turnstile widget:", error);
      }
    }
  }

  loadTurnstileScript = () => {
    // Check if Turnstile script is already loaded
    if (window.turnstile) {
      this.renderTurnstile();
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
    if (existingScript) {
      // Script is loading, wait for it
      existingScript.addEventListener("load", this.renderTurnstile);
      return;
    }

    // Load the Turnstile script
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log("Cloudflare Turnstile script loaded");
      this.turnstileLoaded = true;
      this.renderTurnstile();
    };
    script.onerror = () => {
      console.error("Failed to load Cloudflare Turnstile script");
      this.setState({
        error: "Failed to load security verification. Please refresh the page.",
      });
      if (this.props.onError) {
        this.props.onError(new Error("Failed to load Turnstile script"));
      }
    };
    document.body.appendChild(script);
  };

  renderTurnstile = () => {
    if (!window.turnstile || !this.containerRef.current) {
      return;
    }

    // Prevent double rendering
    if (this.state.widgetId !== null) {
      return;
    }

    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

    if (!siteKey) {
      console.error("REACT_APP_TURNSTILE_SITE_KEY is not configured");
      this.setState({
        error: "Turnstile is not configured properly",
      });
      if (this.props.onError) {
        this.props.onError(new Error("Missing Turnstile site key"));
      }
      return;
    }

    try {
      const widgetId = window.turnstile.render(this.containerRef.current, {
        sitekey: siteKey,
        theme: this.props.theme || "auto",
        size: this.props.size || "normal",
        action: this.props.action,
        cData: this.props.cData,
        callback: this.handleSuccess,
        "error-callback": this.handleError,
        "expired-callback": this.handleExpire,
        "timeout-callback": this.handleTimeout,
      });

      this.setState({
        widgetId,
        isVerifying: true,
      });
    } catch (error) {
      console.error("Error rendering Turnstile widget:", error);
      this.setState({
        error: "Failed to initialize security verification",
      });
      if (this.props.onError) {
        this.props.onError(error);
      }
    }
  };

  handleSuccess = (token) => {
    console.log("Turnstile verification successful");
    this.setState({
      isVerifying: false,
      isVerified: true,
      error: null,
    });

    if (this.props.onVerify) {
      this.props.onVerify(token);
    }
  };

  handleError = (error) => {
    console.error("Turnstile verification error:", error);
    this.setState({
      isVerifying: false,
      isVerified: false,
      error: "Security verification failed. Please try again.",
    });

    if (this.props.onError) {
      this.props.onError(new Error("Turnstile verification failed"));
    }
  };

  handleExpire = () => {
    console.log("Turnstile token expired");
    this.setState({
      isVerifying: false,
      isVerified: false,
      error: "Security verification expired. Please verify again.",
    });

    if (this.props.onExpire) {
      this.props.onExpire();
    }

    // Auto-reset the widget
    this.reset();
  };

  handleTimeout = () => {
    console.log("Turnstile verification timeout");
    this.setState({
      isVerifying: false,
      isVerified: false,
      error: "Security verification timed out. Please try again.",
    });

    if (this.props.onError) {
      this.props.onError(new Error("Turnstile timeout"));
    }
  };

  /**
   * Reset the Turnstile widget
   */
  reset = () => {
    if (this.state.widgetId !== null && window.turnstile) {
      try {
        window.turnstile.reset(this.state.widgetId);
        this.setState({
          isVerifying: true,
          isVerified: false,
          error: null,
        });
      } catch (error) {
        console.error("Error resetting Turnstile widget:", error);
      }
    }
  };

  /**
   * Get the current response token
   */
  getResponse = () => {
    if (this.state.widgetId !== null && window.turnstile) {
      try {
        return window.turnstile.getResponse(this.state.widgetId);
      } catch (error) {
        console.error("Error getting Turnstile response:", error);
        return null;
      }
    }
    return null;
  };

  render() {
    const { error, isVerified, isVerifying } = this.state;

    return (
      <div className="turnstile-container">
        <div ref={this.containerRef} className="turnstile-widget" />
        
        {error && (
          <div className="turnstile-error" style={{ 
            color: "#dc3545", 
            fontSize: "14px", 
            marginTop: "10px" 
          }}>
            {error}
          </div>
        )}
        
        {isVerifying && !isVerified && !error && (
          <div className="turnstile-verifying" style={{ 
            color: "#6c757d", 
            fontSize: "14px", 
            marginTop: "10px" 
          }}>
            Verifying security...
          </div>
        )}
        
        {isVerified && (
          <div className="turnstile-verified" style={{ 
            color: "#28a745", 
            fontSize: "14px", 
            marginTop: "10px" 
          }}>
            ✓ Verification complete
          </div>
        )}
      </div>
    );
  }
}

export default CloudflareTurnstile;
